import DOMPurify from "dompurify";
import { marked } from "marked";
import type { AskHelenaTicketRef } from "shared";
import { linkifyTicketRefs } from "./ticketRefLinks";

/** Capturing group so `split` keeps the <pre> blocks in the result array. */
const PRE_BLOCK = /(<pre[\s\S]*?<\/pre>)/gi;

const TABLE_OPEN = /<table(\s[^>]*)?>/gi;
const TABLE_CLOSE = /<\/table>/gi;

/** Class hook the bubble styles as the table's card + horizontal-scroll shell. */
export const MARKDOWN_TABLE_SCROLL_CLASS = "md-table-scroll";

/**
 * Converts an Ask Helena answer (Markdown) to HTML for SafeHtmlRenderer.
 *
 * SafeHtmlRenderer's sanitize() runs `.replace(/\n/g, "<br>")` over everything
 * before DOMPurify, and marked separates block elements with newlines — left
 * alone, every structural newline would become a visible <br> (huge gaps, stray
 * breaks between <li>s, since `br` is in its ALLOWED_TAGS). So we strip those
 * newlines here, before handing the HTML over.
 *
 * `breaks: true` is load-bearing: it makes marked emit real <br> tags for
 * intentional soft line breaks INSIDE paragraphs, so no meaningful bare \n
 * survives and the blanket strip is lossless — every remaining newline sits at
 * a block boundary, adjacent to `>` or `<`.
 *
 * Inside <pre>, newlines ARE the content, so they become `&#10;` entities: the
 * literal-\n regex can't match an entity, and DOMPurify parses it back into a
 * real newline that `white-space: pre` renders. Using <br> there would
 * double-space code blocks.
 *
 * GFM tables get wrapped in a `.md-table-scroll` div so a wide table (Helena
 * routinely answers with 5-6 columns) scrolls inside its own card instead of
 * stretching the chat column. `div`/`class` both survive DOMPurify.
 *
 * `ticketRefs` are the tickets the service resolved out of this answer; each
 * number is linked once the HTML exists, where an already-linked or quoted
 * number can be told apart from a bare one.
 *
 * DOMPurify still runs last, inside SafeHtmlRenderer — the security invariant
 * is unchanged.
 */
export const markdownToHtml = (
  markdown: string,
  ticketRefs?: AskHelenaTicketRef[] | null,
): string => {
  const raw = marked.parse(markdown, { async: false, gfm: true, breaks: true });

  const html = raw
    .split(PRE_BLOCK)
    .map((segment) =>
      segment.toLowerCase().startsWith("<pre")
        ? segment.replace(/\n/g, "&#10;")
        : segment.replace(/\n+/g, ""),
    )
    .join("")
    .replace(
      TABLE_OPEN,
      (table) => `<div class="${MARKDOWN_TABLE_SCROLL_CLASS}">${table}`,
    )
    .replace(TABLE_CLOSE, "</table></div>");

  return linkifyTicketRefs(html, ticketRefs);
};

const INLINE_ALLOWED_TAGS = [
  "a",
  "b",
  "strong",
  "em",
  "i",
  "u",
  "s",
  "del",
  "code",
  "span",
  "br",
  "mark",
  "sub",
  "sup",
];

const INLINE_ALLOWED_ATTR = ["href", "target", "rel", "class"];

/**
 * One table cell's Markdown as inline HTML — the sortable table renders rows
 * itself, so cells never pass through SafeHtmlRenderer and have to be
 * sanitized here. Block tags are dropped: a cell holds inline content only.
 *
 * A ticket list comes back as a table, so this is the path most ticket links
 * actually take.
 */
export const inlineMarkdownToHtml = (
  markdown: string,
  ticketRefs?: AskHelenaTicketRef[] | null,
): string => {
  const raw = marked.parseInline(markdown, { async: false, gfm: true });

  const clean = DOMPurify.sanitize(raw, {
    ALLOWED_TAGS: INLINE_ALLOWED_TAGS,
    ALLOWED_ATTR: INLINE_ALLOWED_ATTR,
    ADD_ATTR: ["target"],
  });

  // Linked before the `target` patch below, so a ticket link picks it up too.
  return linkifyTicketRefs(clean, ticketRefs).replace(
    /<a\s/gi,
    '<a target="_blank" rel="noopener noreferrer" ',
  );
};
